import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
  ClockIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: EyeIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure checked against Reg Z, UDAAP, Reg DD, ECOA, Fair Housing Act, and more. AI-powered detection with deterministic rules engine verification. Examiner-ready audit trail on every check — no more manual spreadsheets.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'Validate, clean, and prepare LAR data for submission. 170+ CFPB edit checks across syntactical, validity, quality, and macro-quality categories with plain-language explanations. Field mapping templates for LOS imports. Data quality scoring and submission-ready exports.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Full IFLEP-aligned Fair Lending Dashboard with 21 risk factors across underwriting, pricing, steering, redlining, and marketing. Statistical testing — chi-square, Fisher\'s exact, Welch\'s t-test — with regulator-specific methodology for FDIC-supervised institutions. Validated by a former Federal Reserve fair lending subject matter expert.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Output formatted for FDIC examination standards with full methodology documentation.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of enforcement actions and regulatory updates from CFPB, OCC, and FDIC. Alerts when new guidance or enforcement patterns affect your compliance program. No more manually tracking the Federal Register.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Module',
    description:
      'Assessment area delineation with census tract demographics across your Kansas markets. Lending Test analysis pulling from HMDA origination data. Community development activity tracking. CRA health scoring, gap analysis, and exam readiness assessment.',
  },
];

const painPoints = [
  {
    icon: ClockIcon,
    title: 'Weeks of Manual Work, Compressed',
    description:
      'Fair lending analysis that takes consultants weeks — statistical testing across 21 risk factors, 9 prohibited basis groups, and 5 loan purpose segments — runs in minutes. Your team reviews findings instead of building spreadsheets.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Exam-Ready From Day One',
    description:
      'Every output is formatted for what FDIC examiners actually evaluate. One-click exam packages with statistical findings, methodology documentation, and supporting data. Board reports that pull from every compliance module automatically.',
  },
  {
    icon: CpuChipIcon,
    title: 'AI That Works Inside Your Workflow',
    description:
      'Clarid integrates into Microsoft Copilot, Claude, and ChatGPT through MCP (Model Context Protocol). Your team can run compliance checks and pull analytics from the tools they already use — no new portal to learn.',
  },
  {
    icon: ScaleIcon,
    title: 'Built for Community Banks, Not Enterprise',
    description:
      'Designed for institutions where 1-3 people manage HMDA, fair lending, marketing compliance, CRA, and regulatory change simultaneously. Clarid doesn\'t require a compliance department of 20 — it\'s built for teams like yours.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA Data Flows Into Fair Lending Automatically',
    description: 'Upload your LAR once. It feeds directly into denial rate analysis, pricing disparities, geographic patterns, and CRA lending tests — no re-upload, no re-entry.',
  },
  {
    title: 'Fair Lending Findings Feed Exam Packages',
    description: 'Statistical findings, risk levels, and affected demographic groups are automatically pulled into one-click examiner-ready ZIP exports with full methodology documentation.',
  },
  {
    title: 'CRA Lending Analysis Uses the Same HMDA Data',
    description: 'CRA lending tests pull origination volumes and geographic distribution from the same LAR data, with assessment area mapping across your Larned, Overland Park, and Pratt markets.',
  },
  {
    title: 'Board Reports Aggregate Everything',
    description: 'Executive-level reports pull marketing compliance stats, fair lending findings, CRA health scores, regression results, and regulatory updates into a single cross-module view. No more assembling board packages from five different systems.',
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives mapped, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'Today\'s Conversation', description: 'Walk through the platform and explore how it fits your compliance workflow' },
  { num: 2, title: 'Pilot with Your Data', description: 'Upload your HMDA LAR and see fair lending analysis, CRA metrics, and exam packaging with your actual numbers' },
  { num: 3, title: 'Team Onboarding', description: 'Get your compliance team set up — typically same-day, no IT project required' },
];

/* ─── Component ─── */
export default function FusionBankPage() {
  useEffect(() => {
    document.title = 'Fusion Bank + Clarid — AI-Powered Compliance for Community Banking';

    const metaTags: Record<string, string> = {
      'og:title': 'Fusion Bank + Clarid — AI-Powered Compliance',
      'og:description': 'AI-native compliance platform built for community banks. HMDA, fair lending, CRA, marketing compliance, and exam preparation — all connected.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Fusion Bank + Clarid',
      'twitter:description': 'AI-powered compliance for community banking.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <img src="/fusion-bank-logo.png" alt="Fusion Bank" className="h-10" />
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-600 mb-8">
              Built for Community Banks
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Your Entire Compliance Program, Connected
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Fusion Bank has built 130 years of trust through local decision-making and
              relationship banking. Clarid helps protect that legacy — automating HMDA validation,
              fair lending analysis, CRA monitoring, marketing compliance, and exam preparation
              in a single connected platform built specifically for community banks.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Problem ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                Compliance Keeps Getting Harder. Your Team Isn't Getting Bigger.
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks face the same regulatory expectations as institutions ten times their
                  size — HMDA reporting, fair lending analysis, CRA examinations, marketing compliance,
                  and a Federal Register that never stops publishing. But you don't have a compliance
                  department of twenty. You have a small team managing all of it.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  The typical approach — spreadsheets for fair lending, a different vendor for HMDA,
                  manual marketing reviews, and board reports assembled from five different sources —
                  works until it doesn't. An examiner asks for something you can't pull quickly. A
                  fair lending analysis takes weeks because it's manual. A marketing piece goes out
                  without compliance review because the process is too slow.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Clarid replaces that patchwork with a single system where every compliance
                  function talks to every other one — and the outputs are what examiners actually want to see.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Why Fusion Bank ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
              What Changes for Your Team
            </h2>
            <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
              Clarid doesn't add work — it removes it. Here's what's different.
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {painPoints.map((point, i) => (
              <ScaleIn key={point.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <point.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{point.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{point.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 4: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-teal-300 mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Everything Connected. Nothing Siloed.
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance tools solve one problem. HMDA validation here, fair lending analysis there,
                marketing compliance somewhere else, board reporting in a spreadsheet. Clarid connects them
                all. One data upload triggers analysis across every compliance domain — and the results surface
                together in exam packages and board reports. No one else does this.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 mt-14">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-teal-400 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 5: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              What's Inside the Platform
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-teal-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Examination Perspective ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert — with decades of combined
                  examination experience across hundreds of community bank examinations — are
                  partners in Clarid. Every rule engine, every workflow, every output is validated
                  against what examiners actually evaluate.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by a Community Bank CMO
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder manages marketing compliance, HMDA reporting, and examiner
                  relationships at a community bank. This platform wasn't designed by engineers
                  guessing at compliance needs — it was built by someone who lives them and who
                  sits across from examiners using the outputs this system generates. The problems
                  it solves are the ones your compliance team deals with every day.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Due Diligence ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                Vendor Due Diligence — Already Done
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                We know community banks need to document AI vendor relationships for examiners. Clarid
                maintains a comprehensive due diligence package aligned with interagency guidance on
                third-party risk management:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Everything your vendor management team needs is ready to share. SOC 2 Type I is on our
                12-month roadmap — we're documenting controls now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Next Steps ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Getting Started
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Contact ─── */}
      <section className="py-24 md:py-32">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-teal-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-teal-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/fusion-bank-logo.png" alt="Fusion Bank" className="h-7 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
